import { useState } from "react";
import useApi from "./useApi";
import { API_ROUTES } from "lib/constants/api-constants";
import { get } from "lodash";

interface SubscriptionState {
  error?: string | null;
  loading: boolean;
  subscriptions: SubscriptionDetails[];
  getSubscriptions: (
    onSuccess?: (subscriptions: SubscriptionDetails[]) => void
  ) => void;
  getSubscriptionById: (
    subscriptionId: string,
    onSuccess?: (subscriptions: SubscriptionDetails) => void
  ) => void;
  createSubscription: (
    subscription: SubscriptionDetails,
    onSuccess?: (subscription: SubscriptionDetails) => void
  ) => void;
  updateSubscription: (
    subscription: SubscriptionDetails,
    onSuccess?: (subscription: SubscriptionDetails) => void
  ) => void;
  buySubscription: (
    subscriptionCheckoutDetails: PractitionerSubscription,
    onSuccess?: (url: string) => void
  ) => void;
  waiveOffSubscription: (
    subscriptionId: string,
    onSuccess?: (url: string) => void
  ) => void;
}

const useSubscriptions = (): SubscriptionState => {
  const [subscriptions, setSubscriptions] = useState<SubscriptionDetails[]>([]);
  const {
    getData: getSubscriptionsRequest,
    loading: getSubscriptionsRequestLoading,
  } = useApi<SubscriptionDetails[]>();
  const {
    getData: getSubscriptionByIdRequest,
    loading: getSubscriptionByIdRequestLoading,
  } = useApi<SubscriptionDetails>();
  const { postData: buySubscriptionRequest, loading: buySubscriptionLoading } =
    useApi<CheckoutSessionResponse>();
  const { postData: createSubscriptionRequest } = useApi<SubscriptionDetails>();
  const { putData: updateSubscriptionRequest } = useApi<SubscriptionDetails>();
  const {
    getData: getWaiveOffSubscriptionRequest,
    loading: getWaiveOffSubscriptionRequestLoading,
  } = useApi<CheckoutSessionResponse>();
  const [error, setError] = useState<string | null>();

  const onFailure = (errorMessage: string) => {
    setError(errorMessage);
  };

  const getSubscriptions = async (
    onSuccess?: (subscriptions: SubscriptionDetails[]) => void
  ) => {
    setError(null);
    await getSubscriptionsRequest(
      API_ROUTES.SUBSCRIPTIONS.base,
      (value) => {
        const response = (value as SubscriptionDetails[]) ?? [];
        setSubscriptions(response);
        onSuccess?.(response);
      },
      onFailure
    );
  };

  const getSubscriptionById = async (
    subscriptionId: string,
    onSuccess?: (subscription: SubscriptionDetails) => void
  ) => {
    setError(null);
    await getSubscriptionByIdRequest(
      `${API_ROUTES.SUBSCRIPTIONS.base}/${subscriptionId}`,
      (response) => {
        setSubscriptions([response]);
        onSuccess?.(response);
      },
      onFailure
    );
  };

  const createSubscription = async (
    subscription: SubscriptionDetails,
    onSuccess?: (subscription: SubscriptionDetails) => void
  ) => {
    setError(null);
    await createSubscriptionRequest(
      API_ROUTES.SUBSCRIPTIONS.base,
      subscription,
      onSuccess,
      onFailure
    );
  };

  const updateSubscription = async (
    subscription: SubscriptionDetails,
    onSuccess?: (subscription: SubscriptionDetails) => void
  ) => {
    setError(null);

    await updateSubscriptionRequest(
      `${API_ROUTES.SUBSCRIPTIONS.base}/${subscription.id}`,
      subscription,
      onSuccess,
      onFailure
    );
  };

  const buySubscription = async (
    subscriptionCheckoutDetails: PractitionerSubscription,
    onSuccess?: (url: string) => void
  ) => {
    setError(null);
    const endPoint = API_ROUTES.SUBSCRIPTIONS.checkout(
      subscriptionCheckoutDetails.practitionerId
    );
    await buySubscriptionRequest(
      endPoint,
      subscriptionCheckoutDetails,
      (checkoutSessionDetails) => onSuccess?.(checkoutSessionDetails?.url),
      onFailure
    );
  };

  const waiveOffSubscription = async (
    subscriptionId: string,
    onSuccess?: (url: string) => void
  ) => {
    setError(null);
    const endPoint =
      API_ROUTES.PRACTITIONERS.waiveOffPractitionerSubscription.replace(
        "{{TRANSACTION_ID}}",
        subscriptionId
      );
    await getWaiveOffSubscriptionRequest(
      endPoint,
      (checkoutSessionDetails) => onSuccess?.(checkoutSessionDetails?.url),
      onFailure
    );
  };

  return {
    subscriptions,
    error,
    loading:
      getSubscriptionsRequestLoading ||
      getSubscriptionByIdRequestLoading ||
      buySubscriptionLoading ||
      getWaiveOffSubscriptionRequestLoading,
    getSubscriptions,
    getSubscriptionById,
    createSubscription,
    updateSubscription,
    buySubscription,
    waiveOffSubscription,
  };
};
export default useSubscriptions;
